import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    IColumn,
    DefaultButton,
    TextField,
    ShimmeredDetailsList,
    SelectionMode,
} from "office-ui-fabric-react";
import * as microsoftTeams from "@microsoft/teams-js";
import {get, hostserver, post} from "../api";
import {AppProxyDetails} from "../models";
import {ApplicationProxies} from "./components/ApplicationProxies";
import {AppProxy} from "../models";

export interface IAppProxyLoginState extends ITeamsBaseComponentState {
    tenantId: string;
    hasCredentials: boolean;
    authenticatedToSp: boolean;
    loading: boolean;
    isProcessing: boolean;
    error: string;
    message: string;
    spHostUrl: string;
    appProxies: AppProxy[];
    proxy: AppProxy;
    isInEdit: boolean;
    isInAdd: boolean;
    editProxy: boolean;
}

export interface IAppProxyLoginProps {}

export interface ApiErrorResponse {
    Message?: string;
    ExceptionMessage?: string;
    ExceptionType?: string;
    StackTrace?: string;
}

export class AppProxyLogin extends TeamsBaseComponent<IAppProxyLoginProps, IAppProxyLoginState> {
    constructor(props: IAppProxyLoginProps) {
        super(props);
        this.state = {
            theme: null,
            tenantId: this.getQueryVariable("tenantId"),
            hasCredentials: false,
            authenticatedToSp: document.cookie.indexOf("ConConAppProxy=") != -1 ? true : false,
            loading: true,
            isProcessing: false,
            error: "",
            message: "",
            spHostUrl: this.getQueryVariable("spHostUrl"),
            appProxies: [],
            proxy: null, // state to hold new proxy app details or the record we would like to update
            isInEdit: false, // state to check if the details is in edit
            isInAdd: false, // state to check if we need to add a new app
            editProxy: false, // state to check if we are trying to update an app record
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
    }

    public async componentDidMount(): Promise<void> {
        if (this.state.authenticatedToSp) {
            if (!this.state.spHostUrl || !this.state.tenantId) {
                this.setState({error: "No SharePoint host URL or tenant id supplied."});
            } else {
                this.getAppProxyDetails();
            }
        }
    }

    private getAppProxyDetails = async () => {
        await get("/api/teamsdmsintegration/GetAppProxyInfo?spHostUrl=" + this.state.spHostUrl)
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    return r.json();
                } else {
                    this.setState({error: "Error getting AppProxy details. Reload the page and try again."});
                }
            })
            .then((response) => {
                const hasCredentials = response as AppProxyDetails;

                this.setState({
                    hasCredentials: hasCredentials.CredentialsSet,
                    loading: false,
                    appProxies: hasCredentials.ProxyDetails,
                });
            });
    };

    private setAppProxyDetails = async () => {
        if(!this.validateAppProxies())
        {
            this.setState({error: "Unable to save. Please check the input values and try again."});
            return;
        }

        this.setState({ isProcessing: true }, async () => {
            try {
                const res =  await post("/api/teamsdmsintegration/SetAppProxyInfo", {
                    SpHostUrl: this.state.spHostUrl,
                    ProxyDetails: this.state.appProxies,
                })
                
                if (res.status.toString().startsWith("2")) {
                    this.setState({
                        hasCredentials: true,
                        proxy: null,
                        isInEdit: false,
                        isInAdd: false,
                        message: "AppProxy Details saved successfully.",
                        error: "",
                        isProcessing: false
                    });
                } else {
                    const errorJson: ApiErrorResponse = await res.json(); 
                    const errorMessage = errorJson.ExceptionMessage || "Error setting AppProxy details. Please check the input values and try again.";
                    this.setState({ 
                        error: errorMessage, 
                        isInEdit: true, 
                        isProcessing: false 
                    });
                }
            } catch(err){
                this.setState({
                    error: "An unexpected error occurred. Please try again.",
                    isProcessing: false,
                    isInEdit: true
                });
            }
        });
 
        
    };

    private validateAppProxies() {
        if (!Array.isArray(this.state.appProxies)) return false;

        return this.state.appProxies.every(proxy => 
            proxy &&
            typeof proxy.AppId === 'string' && proxy.AppId != "" &&
            typeof proxy.Username === 'string' && proxy.Username != "" &&
            typeof proxy.InternalUrl === 'string' && proxy.InternalUrl != "" &&
            typeof proxy.ExternalUrl === 'string' && proxy.ExternalUrl != ""
        );
    }

    private doSPLogin() {
        const loginUrl =
            hostserver() +
            `/DMSConnector/StartUserLoginFlow?tenantId=${this.state.tenantId},appProxy,${this.state.spHostUrl}`;
        window.location.replace(loginUrl);
    }

    private deleteAppProxyInfo = async () => {        
        this.setState({ isProcessing: true }, async() => {
            try{
                await fetch(hostserver() + "/api/teamsdmsintegration/DeleteAllAppProxies?spHostUrl=" + this.state.spHostUrl, {
                    method: "DELETE",
                })
                .then((r) => {
                    if (r.status.toString().startsWith("2")) {
                        return r.json();
                    } else {
                        this.setState({error: "Error deleting proxy details. Reload the page and try again."});
                    }
                })
                .then((response) => {
                    if (response) {
                        this.setState({
                            error: response, 
                            isProcessing: false 
                        });
                    } else {
                        this.setState({
                            hasCredentials: false,
                            error: "",
                            appProxies: [],
                            isInEdit: false, 
                            isProcessing: false 
                        });
                    }
                });
            }
            catch(err){
                this.setState({
                    error: "An unexpected error occurred. Please try again.",
                    isProcessing: false
                });
            }
            
        });
    };

    private cancelAddNewAppProxy = async () => {
        this.setState({proxy: null, isInAdd: false, error: "", editProxy: false});
    };

    private deleteAppProxy = async (item: AppProxy) => {
        if (!item.IsNew) {
            this.setState({ isProcessing: true }, async () =>{
                try{
                    await fetch(
                        hostserver() +
                            `/api/teamsdmsintegration/DeleteAppProxyById?spHostUrl=${this.state.spHostUrl}&appId=${item.AppId}`,
                        {
                            method: "DELETE",
                        }
                    )
                    .then((r) => {
                        if (r.status.toString().startsWith("2")) {
                            return r.json();
                        } else {
                            this.setState({error: "Error deleting AppProxy details. Reload the page and try again."});
                        }
                    })
                    .then((response) => {
                        if (response) {
                            this.setState({
                                error: response, 
                                isProcessing: false 
                            });
                        } else {
                            const proxies = this.state.appProxies.filter((q) => q.AppId != item.AppId);
                            this.setState({
                                appProxies: proxies, 
                                isProcessing: false 
                            });
                        }
                    });       
                }
                catch(err){
                    this.setState({
                        error: "An unexpected error occurred. Please try again.",
                        isProcessing: false
                    });
                }                
            });
        } else {
            const proxies = this.state.appProxies.filter((q) => q.AppId != item.AppId);
            this.setState({appProxies: proxies});
        }
    };

    private startEditAppProxyDetails = async () => {
        this.setState({isInEdit: true, error: ""});
    };

    private cancelEditAppProxyDetails = async () => {
        this.setState({isInEdit: false});
    };

    private startEditProxy = async (item: AppProxy) => {
        this.setState({proxy: item as AppProxy, editProxy: true, error: ""});
    };

    private getColumns(): IColumn[] {
        return [
            {
                key: "appIdColumn",
                name: "Application Id",
                minWidth: 300,
                maxWidth: 400,
                isResizable: true,
                data: "string",
                onRender: (item: AppProxy) => {
                    return (
                        <div title={item.AppId} style={{fontSize: "15px"}}>
                            {item.AppId}
                        </div>
                    );
                },
            },
            {
                key: "usernameColumn",
                name: "Service Account Username",
                minWidth: 300,
                maxWidth: 400,
                isResizable: true,
                data: "string",
                onRender: (item: AppProxy) => {
                    return (
                        <div title={item.Username} style={{fontSize: "15px"}}>
                            {item.Username}
                        </div>
                    );
                },
            },
            {
                key: "passwordColumn",
                name: "Password",
                minWidth: 300,
                maxWidth: 400,
                isResizable: true,
                data: "string",
                onRender: (item: AppProxy) => {
                    return (
                        <div title={item.Password} style={{fontSize: "15px"}}>
                            {item.Password}
                        </div>
                    );
                },
            },
            {
                key: "internalUrlColumn",
                name: "Internal URL",
                minWidth: 300,
                maxWidth: 400,
                isResizable: true,
                data: "string",
                onRender: (item: AppProxy) => {
                    return (
                        <div title={item.InternalUrl} style={{fontSize: "15px"}}>
                            {item.InternalUrl}
                        </div>
                    );
                },
            },
            {
                key: "externalUrlColumn",
                name: "External URL",
                minWidth: 300,
                maxWidth: 400,
                isResizable: true,
                data: "string",
                onRender: (item: AppProxy) => {
                    return (
                        <div title={item.ExternalUrl} style={{fontSize: "15px"}}>
                            {item.ExternalUrl}
                        </div>
                    );
                },
            },
            {
                key: "editColumn",
                name: "",
                minWidth: 200,
                maxWidth: 200,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    if (this.state.isInEdit) {
                        return (
                            <div className="sharedColumnStopSharingCell">
                                <DefaultButton
                                    style={{width: "125px"}}
                                    onClick={() => this.startEditProxy(item)}
                                    text={"Edit"}
                                />
                            </div>
                        );
                    }
                },
            },
            {
                key: "buttonsColumn",
                name: "",
                minWidth: 200,
                maxWidth: 200,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    if (this.state.isInEdit) {
                        return (
                            <div className="sharedColumnStopSharingCell">
                                <DefaultButton
                                    style={{width: "125px"}}
                                    onClick={() => this.deleteAppProxy(item)}
                                    text={"Delete"}
                                />
                            </div>
                        );
                    }
                },
            },
        ];
    }

    private createGuid = () => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };

    private addNewRow = () => {
        const id = this.createGuid();
        this.setState({
            proxy: {AppId: "", Username: "", Password: "",  ExternalUrl: "", InternalUrl: "", Id: id, IsNew: true} as AppProxy,
            isInAdd: true,
            error: ""
        });
    };

    private trimProxyWhiteSpace() {
        const appProxy = this.state.proxy;
        appProxy.AppId = appProxy.AppId.trim();
        appProxy.Username = appProxy.Username.trim();
        appProxy.ExternalUrl = appProxy.ExternalUrl.trim();
        appProxy.InternalUrl = appProxy.InternalUrl.trim();

        this.setState({proxy: appProxy});
    }

    private addUpdateProxy = async () => {
        this.trimProxyWhiteSpace();

        if (this.state.isInAdd) {
            const proxies = this.state.appProxies || ([] as AppProxy[]);
            if (this.state.proxy) {
                if (!this.state.proxy.AppId || !this.state.proxy.ExternalUrl || !this.state.proxy.InternalUrl) {
                    this.setState({error: "You must set all required values."});
                    return;
                }
                const itm = this.state.proxy;
                itm.IsNew = true;
                proxies.push(itm);
            }

            this.setState({proxy: null, appProxies: proxies, isInAdd: false, error: ""});
        } else {
            const proxies = this.state.appProxies as AppProxy[];

            const objIndex = proxies.findIndex((obj) => obj.Id == this.state.proxy.Id);

            proxies[objIndex] = this.state.proxy;

            this.setState({proxy: null, appProxies: proxies, editProxy: false});
        }
    };

    private renderDeprecationNotice() {
        return (
            <div style={{marginBottom: "20px"}}>
                <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
                    This settings page is obsolete and will be removed in a future release. Content
                    Connector app proxy settings for iManage should now be configured in{" "}
                    <b>ICP Administration &gt; Integrations &gt; Content Connector &gt; iManage App Proxies</b>.
                </MessageBar>
            </div>
        );
    }

    public render() {
        if (!this.state.authenticatedToSp) {
            return (
                <div>
                    {this.renderDeprecationNotice()}
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={false}
                        actions={
                            <div>
                                <PrimaryButton onClick={this.doSPLogin.bind(this)} text={"Log In"} />
                            </div>
                        }
                    >
                        You are not currently logged in. Log in to access file content.
                    </MessageBar>
                </div>
            );
        }

        if (this.state.loading) {
            return (
                <div>
                    {this.renderDeprecationNotice()}
                    <Spinner />
                </div>
            );
        } else {
            if (!this.state.spHostUrl || !this.state.tenantId) {
                return (
                    <div className="center" style={{width: "90%"}}>
                        {this.renderDeprecationNotice()}
                        <div className="container" style={{width: "100%", boxSizing: "border-box", display: "flow-root"}}>
                            <h1 style={{fontSize: "20px"}}>iManage™ AppProxy Details</h1>
                            <br />
                            <hr />
                            <br />
                            {this.state.error && <h4 style={{color: "red"}}>{this.state.error}</h4>}
                        </div>
                    </div>
                );
            } else if (!this.state.hasCredentials || this.state.isInEdit) {
                return (
                    <div className="center" style={{width: "90%"}}>
                        {this.renderDeprecationNotice()}
                        <div className="container" style={{width: "100%", boxSizing: "border-box", display: "flow-root"}}>
                        <h1 style={{fontSize: "20px"}}>iManage™ AppProxy Details</h1>
                        <br />
                        <hr />
                        <br />

                        {this.state.error && (
                            <div className="padded-row" style={{color: "red", paddingBottom: "30px"}}>
                                {this.state.error}
                            </div>
                        )}

                        <div
                            className="row"
                            style={this.state.isInAdd || this.state.editProxy ? {display: "none"} : {}}
                        >
                            <div className="col-12">
                                <div className="row padded-row">
                                    <div className="col-6">
                                        <PrimaryButton text={"Add AppProxy"} onClick={this.addNewRow} />
                                    </div>
                                </div>

                                <div className="row padded-row">
                                    <div className="col-12">
                                        {this.state.appProxies &&
                                            (this.state.appProxies.length > 0 || this.state.isInEdit) && (
                                                <ShimmeredDetailsList
                                                    items={this.state.appProxies}
                                                    enableShimmer={false}
                                                    selectionMode={SelectionMode.none}
                                                    columns={this.getColumns()}
                                                    ariaLabelForShimmer="Content is being fetched"
                                                    ariaLabelForGrid="Application Urls"
                                                    isHeaderVisible={true}
                                                />
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className="row"
                            style={this.state.isInAdd || this.state.editProxy ? {} : {display: "none"}}
                        >
                            <table id="tab_logic">
                                <thead>
                                    <tr>
                                        <th className="text-left" colSpan={3}>
                                            {this.state.isInAdd ? "New" : "Edit "} AppProxy
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr id="newRecord">
                                        <td>
                                            <TextField
                                                required={true}
                                                placeholder="Application Id"
                                                onChange={(_, val) =>
                                                    this.setState({
                                                        proxy: {
                                                            AppId: val,
                                                            Username: this.state.proxy.Username,
                                                            Password: this.state.proxy.Password,
                                                            InternalUrl: this.state.proxy.InternalUrl,
                                                            ExternalUrl: this.state.proxy.ExternalUrl,
                                                            Id: this.state.proxy.Id,
                                                        } as AppProxy,
                                                    })
                                                }
                                                autoComplete="off"
                                                className={"authinput"}
                                                value={this.state.proxy === null ? "" : this.state.proxy.AppId}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                required={true}
                                                placeholder="Service Account Username"
                                                onChange={(_, val) =>
                                                    this.setState({
                                                        proxy: {
                                                            AppId: this.state.proxy.AppId,
                                                            Username: val,
                                                            Password: this.state.proxy.Password,
                                                            InternalUrl: this.state.proxy.InternalUrl,
                                                            ExternalUrl: this.state.proxy.ExternalUrl,
                                                            Id: this.state.proxy.Id,
                                                        } as AppProxy,
                                                    })
                                                }
                                                autoComplete="off"
                                                className={"authinput"}
                                                value={this.state.proxy === null ? "" : this.state.proxy.Username}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                required={true}
                                                placeholder="Password"
                                                onChange={(_, val) =>
                                                    this.setState({
                                                        proxy: {
                                                            AppId: this.state.proxy.AppId,
                                                            Username: this.state.proxy.Username,
                                                            Password: val,
                                                            InternalUrl: this.state.proxy.InternalUrl,
                                                            ExternalUrl: this.state.proxy.ExternalUrl,
                                                            Id: this.state.proxy.Id,
                                                        } as AppProxy,
                                                    })
                                                }
                                                autoComplete="off"
                                                className={"authinput"}
                                                value={this.state.proxy === null ? "" : this.state.proxy.Password}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                required={true}
                                                placeholder="Internal Url"
                                                onChange={(_, val) =>
                                                    this.setState({
                                                        proxy: {
                                                            AppId: this.state.proxy.AppId,
                                                            Username: this.state.proxy.Username,
                                                            Password: this.state.proxy.Password,
                                                            InternalUrl: val,
                                                            ExternalUrl: this.state.proxy.ExternalUrl,
                                                            Id: this.state.proxy.Id,
                                                        } as AppProxy,
                                                    })
                                                }
                                                autoComplete="off"
                                                className={"authinput"}
                                                value={this.state.proxy === null ? "" : this.state.proxy.InternalUrl}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                required={true}
                                                placeholder="External Url"
                                                onChange={(_, val) =>
                                                    this.setState({
                                                        proxy: {
                                                            AppId: this.state.proxy.AppId,
                                                            Username: this.state.proxy.Username,
                                                            Password: this.state.proxy.Password,
                                                            InternalUrl: this.state.proxy.InternalUrl,
                                                            ExternalUrl: val,
                                                            Id: this.state.proxy.Id,
                                                        } as AppProxy,
                                                    })
                                                }
                                                autoComplete="off"
                                                className={"authinput"}
                                                value={this.state.proxy === null ? "" : this.state.proxy.ExternalUrl}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div style={{float: "right"}}>
                                <PrimaryButton
                                    onClick={this.addUpdateProxy}
                                    text={this.state.isInAdd ? "Add" : "Update"}
                                    className={"authinput"}
                                />
                                &nbsp;&nbsp;&nbsp;
                                <DefaultButton
                                    onClick={this.cancelAddNewAppProxy}
                                    text={"Cancel"}
                                    className={"authinput"}
                                />
                            </div>
                        </div>

                        <div
                            className="row padded-row"
                            style={this.state.isInAdd || this.state.editProxy ? {display: "none"} : {}}
                        >
                            <div className="col-8" style={{paddingTop: "50px"}}>
                                <PrimaryButton
                                    onClick={this.setAppProxyDetails}
                                    text={"Save AppProxy Settings"}
                                    className={"authinput"}
                                    disabled={this.state.isProcessing}
                                />
                                &nbsp;&nbsp;&nbsp;
                                <DefaultButton
                                    onClick={this.cancelEditAppProxyDetails}
                                    text={"Cancel"}
                                    className={"authinput"}
                                    disabled={this.state.isProcessing}
                                />
                            </div>
                        </div>
                    </div>
                    </div>
                );
            } else {
                return (
                    <div className="center" style={{width: "90%"}}>
                        {this.renderDeprecationNotice()}
                        <div className="container" style={{width: "100%", boxSizing: "border-box", display: "flow-root"}}>
                        <h1 style={{fontSize: "20px"}}>iManage™ AppProxy Details</h1>
                        <br />
                        <hr />
                        <br />

                        {this.state.error && (
                            <div className="padded-row" style={{color: "red", paddingBottom: "30px"}}>
                                {this.state.error}
                            </div>
                        )}


                        {this.state.message && (
                            <MessageBar
                                messageBarType={MessageBarType.info}
                                isMultiline={false}
                            >
                               {this.state.message}
                            </MessageBar>
                        )}

                        <ApplicationProxies
                            appProxies={this.state.appProxies}
                            deleteAppProxy={this.deleteAppProxy}
                            startEditAppProxyDetails={this.startEditAppProxyDetails}
                            deleteAppProxyInfo={this.deleteAppProxyInfo}
                            disabled={false}
                            columns={this.getColumns()}
                        />
                        </div>
                    </div>
                );
            }
        }
    }
}
